import React, { useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { ref, push, set, update } from 'firebase/database';
import { database } from '../../../../firebase';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../contexts/LanguageContext';
import nationalizationService, {
  addNationalizationLifecycleEventToUpdates,
  announceNationalizationWorkforceChange
} from '../../../../services/nationalizationService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || "http://localhost:8000";

const AddBonusModal = ({ employeeId, companyUID, employeeName, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation(['myteam', 'common']);
  const { isRTL, language } = useLanguage();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [bonusType, setBonusType] = useState('performance');
  const [paymentDate, setPaymentDate] = useState('');
  const [saving, setSaving] = useState(false);
  const safeEmployeeName = employeeName || t('drawerModals.common.unknownEmployee', 'Unknown Employee');

  // Set default payment date to today
  React.useEffect(() => {
    const today = new Date();
    setPaymentDate(today.toISOString().split('T')[0]);
  }, []);

  const handleSave = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert(t('drawerModals.bonus.errors.amount', 'Please enter a valid bonus amount'));
      return;
    }

    setSaving(true);
    try {
      const bonusData = {
        amount: parseFloat(amount),
        reason: reason || '',
        type: bonusType,
        paymentDate: paymentDate || new Date().toISOString().split('T')[0],
        approvedBy: currentUser?.displayName || currentUser?.email || 'HR Team',
        approvedByUID: currentUser?.uid,
        approvedAt: Date.now(),
        status: 'approved'
      };

      const bonusRef = push(ref(database, `companies/${companyUID}/employees/${employeeId}/compensation/bonuses`));
      const source = 'employee_bonus_added';
      const workforceUpdates = {
        [`companies/${companyUID}/employees/${employeeId}/compensation/bonuses/${bonusRef.key}`]: bonusData
      };
      // Some nationalization programs may count wage components differently.
      // Over-invalidate on bonus changes and let the protected adapter decide.
      const lifecycleEvent = addNationalizationLifecycleEventToUpdates(workforceUpdates, {
        companyUID,
        employeeId,
        source,
        changeKinds: ['compensation']
      });
      await update(ref(database), workforceUpdates);
      let lifecycleSync = null;
      try {
        lifecycleSync = await nationalizationService.syncEmployeeLifecycle(companyUID, employeeId, {
          eventId: lifecycleEvent.eventId,
          source,
          changeKinds: ['compensation']
        });
      } catch (syncError) {
        console.warn('Nationalization bonus review is pending:', syncError);
      }
      announceNationalizationWorkforceChange({
        companyUID,
        source,
        recalculated: false,
        syncPending: !lifecycleSync
      });

      const localizedBonusType = t(`drawerModals.bonus.types.${bonusType}`, bonusType);
      const localizedAmount = parseFloat(amount).toLocaleString(language === 'ar' ? 'ar' : 'en-US');
      const notificationMessage = reason
        ? t('drawerModals.bonus.notificationMessageWithReason', {
          type: localizedBonusType,
          amount: localizedAmount,
          reason,
          defaultValue: 'You have been awarded a {{type}} bonus of ${{amount}}: {{reason}}.'
        })
        : t('drawerModals.bonus.notificationMessage', {
          type: localizedBonusType,
          amount: localizedAmount,
          defaultValue: 'You have been awarded a {{type}} bonus of ${{amount}}.'
        });
      const notificationTitle = t('drawerModals.bonus.notificationTitle', 'Bonus awarded');

      // Send Notification to Employee
      const notifRef = push(ref(database, `companies/${companyUID}/employees/${employeeId}/notifications`));
      await set(notifRef, {
        title: notificationTitle,
        message: notificationMessage,
        type: 'success',
        timestamp: Date.now(),
        read: false
      });

      // Send Email Notification
      try {
        if (!currentUser) throw new Error('Authentication required');
        const token = await currentUser.getIdToken();
        await fetch(`${BACKEND_URL}/send-notification-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            companyId: companyUID,
            employeeId,
            subject: notificationTitle,
            message: notificationMessage,
            type: 'success'
          })
        });
      } catch (e) {
        console.error('Email notification failed:', e);
      }

      alert(t('drawerModals.bonus.success', 'Bonus added successfully'));
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Add bonus failed:', error);
      alert(t('drawerModals.bonus.failure', 'Failed to add bonus'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '500px',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left'
        }}
      >
        <div className="modal-header">
          <h2>{t('drawerModals.bonus.title', 'Add Bonus')}</h2>
          <button className="close" onClick={onClose}>&times;</button>
        </div>
        <div className="form-section">
          {/* Employee Name */}
          <div className="input-group" style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.common.employee', 'Employee')}
            </label>
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              color: 'var(--app-heading, #031128)'
            }}>
              {safeEmployeeName}
            </div>
          </div>

          {/* Bonus Type */}
          <div className="input-group" style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.bonus.type', 'Bonus Type')}
            </label>
            <select
              value={bonusType}
              onChange={(e) => setBonusType(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'var(--app-heading, #031128)',
                backgroundColor: '#FFFFFF',
                cursor: 'pointer'
              }}
            >
              <option value="performance">{t('drawerModals.bonus.types.performance', 'Performance Bonus')}</option>
              <option value="signing">{t('drawerModals.bonus.types.signing', 'Signing Bonus')}</option>
              <option value="retention">{t('drawerModals.bonus.types.retention', 'Retention Bonus')}</option>
              <option value="referral">{t('drawerModals.bonus.types.referral', 'Referral Bonus')}</option>
              <option value="holiday">{t('drawerModals.bonus.types.holiday', 'Holiday Bonus')}</option>
              <option value="spot">{t('drawerModals.bonus.types.spot', 'Spot Bonus')}</option>
              <option value="other">{t('drawerModals.bonus.types.other', 'Other')}</option>
            </select>
          </div>

          {/* Amount */}
          <div className="input-group" style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.bonus.amount', 'Amount (USD)')} <span style={{ color: 'var(--app-tone-eb4040, #EB4040)' }}>*</span>
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              min="0"
              step="0.01"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'var(--app-heading, #031128)'
              }}
            />
          </div>

          {/* Payment Date */}
          <div className="input-group" style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.bonus.paymentDate', 'Payment Date')}
            </label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'var(--app-heading, #031128)'
              }}
            />
          </div>

          {/* Reason */}
          <div className="input-group" style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.bonus.reason', 'Reason (Optional)')}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('drawerModals.bonus.reasonPlaceholder', 'Reason for bonus...')}
              rows={3}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'var(--app-heading, #031128)',
                resize: 'vertical'
              }}
            />
          </div>
        </div>
        <div className="modal-footer" style={{
          padding: '20px 32px',
          borderTop: '1px solid #E0E0E0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          flexDirection: isRTL ? 'row-reverse' : 'row'
        }}>
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '12px 24px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: "var(--app-neutral-ink, #031128)",
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
            data-light-control={"quiet"}>
            {t('common:buttons.cancel', 'Cancel')}
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || !amount || parseFloat(amount) <= 0}
            style={{ '--light-control-fill': saving || !amount || parseFloat(amount) <= 0 ? '#CCCCCC' : '#40EB6E',
              padding: '12px 24px',
              backgroundColor: saving || !amount || parseFloat(amount) <= 0 ? '#CCCCCC' : '#40EB6E',
              border: 'none',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: '#FFFFFF',
              cursor: saving || !amount || parseFloat(amount) <= 0 ? 'not-allowed' : 'pointer'
            }}
            data-light-control={saving || !amount || parseFloat(amount) <= 0 ? "quiet" : "filled"}>
            {saving ? t('drawerModals.bonus.saving', 'Saving...') : t('drawerModals.bonus.add', 'Add Bonus')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddBonusModal;
